/**
 * CopilotChat — shared chat surface used by both the drawer launcher
 * (CopilotDrawer) and the full-page route (CopilotPage).
 *
 * Behaviour:
 *  - Sends prompts to POST /copilot/chat with the current tenant_id.
 *  - Renders bot narration + an action card when the LLM proposes a tool
 *    invocation (the orchestrator already executed the tool server-side;
 *    we just summarise the result + link to the proposal review).
 *  - Stores the session_id locally so the same conversation continues
 *    until the drawer is closed.
 *  - Shows a transparency footer: "FWM Copilot · AI-generated.
 *    Requires approval before publication."
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sparkles, Send, FileText, AlertCircle, CheckCircle2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';

// Page-context aware suggestion bundles. Highest-specificity match wins.
const PAGE_CONTEXT_SUGGESTIONS = [
  { test: /\/admin\/schools\/[^/]+.*tab=exams/i, suggestions: [
    'Draft a pop quiz from my latest math lesson',
    'Generate a weekly recap quiz for Year 2',
    'Show students at risk in exams',
  ]},
  { test: /\/admin\/schools\/[^/]+.*tab=(students|attendance)/i, suggestions: [
    'Show students with attendance below 75%',
    'List students at risk this term',
  ]},
  { test: /\/admin\/schools\/[^/]+.*tab=(lms|lessons)/i, suggestions: [
    'Find my recent published lessons',
    'Draft a lesson plan for Romans 8',
  ]},
  { test: /\/admin\/schools\/[^/]+/i, suggestions: [
    'Show students with attendance below 75%',
    'Draft a pop quiz from my latest math lesson',
    'Generate a Bible quiz on Romans 8',
  ]},
];

const SUGGESTIONS_BY_ROLE = {
  teacher: [
    'List the lessons I have taught this week',
    'Show me students with attendance below 75%',
    'Draft a pop quiz from my most recent Hermeneutics lesson',
    'Generate a Bible quiz on Romans 8',
  ],
  school_admin: [
    'Show students at risk across the tenant',
    'Draft a weekly recap quiz for Year 2',
    'Generate a Bible quiz on James 1',
    'Create a 7-day reading plan on Faith',
  ],
  super_admin: [
    'Show the audit log of every Copilot action this week',
    'List my subjects',
    'Draft a Bible reading plan on Joy in Philippians',
  ],
  student: [
    'What are my upcoming lessons?',
    'Generate revision questions on my last topic',
  ],
};

const storageKey = (tenantId, role) => `fwm.copilot.session.${tenantId}.${role}`;

export const CopilotChat = ({ tenantId, userRole = 'school_admin', onProposal }) => {
  const persistedKey = useMemo(() => storageKey(tenantId, userRole), [tenantId, userRole]);

  // Hydrate from localStorage so closing+reopening keeps context.
  const initial = useMemo(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem(persistedKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?.expiresAt && parsed.expiresAt < Date.now()) {
        localStorage.removeItem(persistedKey);
        return null;
      }
      return parsed;
    } catch (_e) { return null; }
  }, [persistedKey]);

  const [messages, setMessages] = useState(initial?.messages || [
    { role: 'system', content: `Welcome. I'm FWM Copilot. I can read your school data, draft lesson plans, and propose quizzes — every action I take is logged and waits for your approval before going live.` },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(initial?.sessionId || null);
  const scrollRef = useRef(null);

  // Iter-372 — accept a prefilled prompt from other widgets via a window
  // CustomEvent. Any caller can dispatch `copilot:prefill` with a `prompt`
  // detail and the input will populate (and optionally auto-send).
  useEffect(() => {
    const onPrefill = (e) => {
      const text = e?.detail?.prompt || '';
      if (!text) return;
      setInput(text);
      // Allow auto-send via {autoSend: true}. Default: just populate so
      // the user can tweak before pressing Send.
      if (e.detail?.autoSend) {
        // small delay so the input visibly shows the prompt before send fires
        setTimeout(() => send(text), 150);
      }
    };
    window.addEventListener('copilot:prefill', onPrefill);
    return () => window.removeEventListener('copilot:prefill', onPrefill);
    // send is defined below — relying on hoisting via fn declaration would
    // miss the latest closure. Using ref pattern would be over-engineered
    // for a one-shot event, so we accept the ESLint warning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist on every change. 24h TTL so old conversations age out.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(persistedKey, JSON.stringify({
        sessionId, messages, expiresAt: Date.now() + 24 * 60 * 60 * 1000,
      }));
    } catch (_e) { /* quota or private-mode — ignore */ }
  }, [sessionId, messages, persistedKey]);

  const suggestions = useMemo(() => {
    if (typeof window !== 'undefined') {
      const url = window.location.pathname + window.location.search;
      const ctxMatch = PAGE_CONTEXT_SUGGESTIONS.find(s => s.test.test(url));
      if (ctxMatch) return ctxMatch.suggestions;
    }
    return SUGGESTIONS_BY_ROLE[userRole] || SUGGESTIONS_BY_ROLE.school_admin;
  }, [userRole]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const prompt = (text ?? input).trim();
    if (!prompt || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: prompt }]);
    setSending(true);
    try {
      const r = await api.post('/copilot/chat', {
        prompt, tenant_id: tenantId, session_id: sessionId,
      });
      const data = r.data || {};
      if (data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, {
        role: 'assistant',
        content: data.narration || '(no response)',
        tool: data.tool,
        command: data.command,
        toolResult: data.tool_result,
        proposalId: data.proposal_id,
        error: data.error,
      }]);
      if (data.proposal_id && onProposal) onProposal(data.proposal_id);
    } catch (e) {
      toast.error(getErrorMessage(e));
      setMessages((m) => [...m, {
        role: 'assistant', content: `Error: ${getErrorMessage(e)}`, error: true,
      }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="copilot-chat">
      <div ref={scrollRef} className="flex-1 overflow-auto px-4 py-3 space-y-3">
        {messages.map((m, i) => (
          <MessageBubble key={i} m={m} />
        ))}
        {sending && (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Sparkles className="h-3 w-3 animate-pulse" /> Thinking…
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="px-4 pb-2 flex flex-wrap gap-1">
          {suggestions.map((s) => (
            <button key={s} onClick={() => send(s)}
              data-testid={`copilot-suggestion-${s.slice(0, 20).replace(/\s+/g, '-')}`}
              className="text-[11px] px-2 py-1 rounded-full bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 transition">
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="border-t bg-white">
        <div className="flex items-end gap-2 px-3 py-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask FWM Copilot… (e.g. draft a pop quiz from my latest math lesson)"
            data-testid="copilot-input"
            disabled={sending}
          />
          <Button size="sm" onClick={() => send()} disabled={sending || !input.trim()}
            data-testid="copilot-send">
            <Send className="h-3.5 w-3.5" />
          </Button>
          {messages.length > 1 && (
            <Button size="sm" variant="ghost"
              title="Clear conversation"
              onClick={() => {
                setMessages([{ role: 'system', content: 'New conversation. Previous context cleared.' }]);
                setSessionId(null);
                try { localStorage.removeItem(persistedKey); } catch (_e) { /* ignore */ }
              }}
              data-testid="copilot-clear">
              <Trash2 className="h-3.5 w-3.5 text-slate-400" />
            </Button>
          )}
        </div>
        <p className="text-[10px] text-slate-400 px-3 pb-2 flex items-center gap-1">
          <Sparkles className="h-2.5 w-2.5" />
          FWM Copilot · AI-generated. Requires approval before publication.
        </p>
      </div>
    </div>
  );
};

const MessageBubble = ({ m }) => {
  if (m.role === 'system') {
    return (
      <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2 leading-relaxed">
        {m.content}
      </div>
    );
  }
  if (m.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-slate-900 text-white rounded-2xl rounded-br-sm px-3 py-2 text-sm max-w-[85%]">
          {m.content}
        </div>
      </div>
    );
  }
  // assistant
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start gap-2">
        <div className="h-6 w-6 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shrink-0">
          <Sparkles className="h-3 w-3 text-white" />
        </div>
        <div className={`text-sm leading-relaxed whitespace-pre-wrap max-w-[85%] ${m.error ? 'text-rose-700' : 'text-slate-800'}`}>
          {m.content}
        </div>
      </div>
      {m.toolResult && <ToolResultCard tr={m.toolResult} tool={m.tool} command={m.command} proposalId={m.proposalId} />}
    </div>
  );
};

const ToolResultCard = ({ tr, tool, command, proposalId }) => {
  const ok = tr?.ok !== false;
  return (
    <div className={`ml-8 border rounded p-2 text-xs space-y-1 ${ok ? 'bg-violet-50 border-violet-200' : 'bg-rose-50 border-rose-200'}`}
         data-testid="copilot-tool-result">
      <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-700">
        {ok ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <AlertCircle className="h-3 w-3 text-rose-600" />}
        {tool || 'tool'}:{command || ''}
      </div>
      {tr?.summary && <p className="text-slate-700">{tr.summary}</p>}
      {tr?.error && <p className="text-rose-700">{tr.error}</p>}
      {proposalId && (
        <a href={`/copilot/proposals/${proposalId}`}
           className="inline-flex items-center gap-1 text-violet-700 hover:underline"
           data-testid={`copilot-review-link-${proposalId}`}>
          <FileText className="h-3 w-3" /> Review proposal →
        </a>
      )}
      {/* For simple read-only listings (students/classes), render compactly */}
      {Array.isArray(tr?.students) && tr.students.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {tr.students.slice(0, 6).map((s) => (
            <li key={s.student_id} className="flex justify-between text-slate-700">
              <span>{s.name || s.student_id}</span>
              <span className="text-slate-500">{s.percentage}%</span>
            </li>
          ))}
        </ul>
      )}
      {Array.isArray(tr?.classes) && tr.classes.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {tr.classes.slice(0, 6).map((c) => (
            <li key={c.id} className="text-slate-700">{c.name} {c.level && `· ${c.level}`}</li>
          ))}
        </ul>
      )}
      {Array.isArray(tr?.lessons) && tr.lessons.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {tr.lessons.slice(0, 6).map((l) => (
            <li key={l.id} className="text-slate-700 flex justify-between gap-2">
              <span className="truncate">{l.title || l.topic || l.id}</span>
              <span className="text-slate-500 shrink-0">{l.subject_code || l.subject_name || ''}</span>
            </li>
          ))}
        </ul>
      )}
      {Array.isArray(tr?.subjects) && tr.subjects.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {tr.subjects.slice(0, 8).map((s) => (
            <li key={s.id} className="text-slate-700 flex justify-between gap-2">
              <span>{s.name}</span>
              <span className="text-slate-500 shrink-0">{s.code}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CopilotChat;
